import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import {
	removePortal,
	shownPortalIds,
} from "@productlane/lib/src/portal-visibility"
import { assertRankUpdates } from "@productlane/lib/src/roadmap-custom-order"

import type { MutatorTx } from "../schema"
import type {
	HideAllVisibleArgs,
	ProjectLinkArgs,
	ProjectLinkUpdateArgs,
	ProjectUnlinkArgs,
	SetPortalSortOrdersArgs,
	UpdateIssueProjectVisibilityBulkArgs,
	UpdateProjectArgs,
} from "../types"

function dateToTimestamp(
	date: Date | string | number | null | undefined,
): number | null {
	if (!date) return null
	if (typeof date === "number") return date
	return typeof date === "string" ? new Date(date).getTime() : date.getTime()
}

const updateProject = async (tx: MutatorTx, args: UpdateProjectArgs) => {
	const { linearStatusId, priority, ...updateData } = args
	await tx.mutate.projects.update({
		...updateData,
		...(linearStatusId && { statusId: linearStatusId }),
		...(priority && { priority: priority.label }),
	})
}

export const projectMutators = {
	update: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: UpdateProjectArgs }) =>
			updateProject(tx, args),
	),
	link: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: ProjectLinkArgs
			ctx: ZeroJwtPayload
		}) => {
			const { linearProject, feedbackId, notUpdateVisibility } = args

			const feedback = await tx.query.feedbacks
				.where("id", "=", feedbackId)
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.one()
				.run()

			if (!feedback) {
				throw new Error("Feedback not found")
			}

			const { linearProjectId: _linearProjectId, ...projectFields } =
				linearProject
			await tx.mutate.projects.update({
				...projectFields,
				isVisible: notUpdateVisibility ? linearProject.isVisible : undefined,
				startDate: dateToTimestamp(linearProject.startDate),
				targetDate: dateToTimestamp(linearProject.targetDate),
				workspaceId: authData.workspaceId,
			})

			const project = await tx.query.projects
				.where("workspaceId", "=", authData.workspaceId)
				.where("id", "=", linearProject.id)
				.one()
				.run()

			if (!project) {
				throw new Error("Project not found")
			}

			await tx.mutate.feedbackToProjects.insert({
				A: args.feedbackId,
				B: project.id,
			})
		},
	),
	linkUpdate: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: ProjectLinkUpdateArgs
			ctx: ZeroJwtPayload
		}) => {
			try {
				// Get the feedback to update
				const [feedback] = await tx.query.feedbacks
					.where("id", "=", args.feedbackId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				if (!feedback) {
					throw new Error("Could not find feedback")
				}

				// Find the customer need
				if (args.projectId) {
					const customerNeeds = await tx.query.customerNeeds
						.where("feedbackId", "=", args.feedbackId)
						.where("projectId", "=", args.projectId)
						.run()

					const customerNeed = customerNeeds[0]

					if (customerNeed) {
						await tx.mutate.customerNeeds.update({
							id: customerNeed.id,
							priority: args.priority,
						})
					}
				}
			} catch (error) {
				// Don't throw to allow server-side to handle the operation
			}
		},
	),
	unlink: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: ProjectUnlinkArgs
			ctx: ZeroJwtPayload
		}) => {
			try {
				// Get the project to validate it exists
				const [project] = await tx.query.projects
					.where("id", "=", args.internalProjectId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				if (!project) {
					throw new Error("Project not found")
				}

				// Get the feedback to validate it exists
				const [feedback] = await tx.query.feedbacks
					.where("id", "=", args.feedbackId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				if (!feedback) {
					throw new Error("Could not find feedback")
				}

				// Remove the feedback-to-project relationship
				const relationships = await tx.query.feedbackToProjects
					.where("A", "=", args.feedbackId)
					.where("B", "=", args.internalProjectId)
					.run()

				for (const relationship of relationships) {
					await tx.mutate.feedbackToProjects.delete({
						A: relationship.A,
						B: relationship.B,
					})
				}
			} catch (error) {
				// Don't throw to allow server-side to handle the operation
			}
		},
	),
	hideAllVisible: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: HideAllVisibleArgs
			ctx: ZeroJwtPayload
		}) => {
			const rows = await tx.query.projects
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.where("isVisible", "=", true)
				.run()
			const portalId = args.portalInstanceId
			const namedPortalIds = portalId
				? (
						await tx.query.portalInstances
							.where("workspaceId", "=", authData.workspaceId)
							.run()
					).map((p) => p.id)
				: []
			for (const row of rows) {
				if (!portalId) {
					await updateProject(tx, { id: row.id, isVisible: false })
					continue
				}
				if (!shownPortalIds(row).includes(portalId)) continue
				await updateProject(tx, {
					id: row.id,
					...removePortal(row, portalId, namedPortalIds),
				})
			}
		},
	),
	bulkChangeVisibility: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: UpdateIssueProjectVisibilityBulkArgs
		}) => {
			for (const id of args.ids) {
				await updateProject(tx, { id, isVisible: args.isVisible })
			}
		},
	),
	setPortalSortOrders: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: SetPortalSortOrdersArgs
			ctx: ZeroJwtPayload
		}) => {
			assertRankUpdates(args.orders, authData.role)
			for (const order of args.orders) {
				await tx.mutate.projects.update({
					id: order.id,
					portalSortOrder: order.portalSortOrder,
				})
			}
		},
	),
}
